import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { PodcastEpisode } from "@ckgo2/shared/types";
import { formatEpisodeDuration } from "@ckgo2/shared";
import { Plus, Pencil, Trash2, Loader2, Mic } from "lucide-react";

export default function PodcastEpisodesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["podcast-episodes"],
    queryFn: () => api.get<ApiResponse<PodcastEpisode[]>>("/api/admin/podcast-episodes"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/podcast-episodes/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["podcast-episodes"] }),
  });

  // `draft|published` nemá sdílenou *_LABELS mapu, takže se nerenderuje přes
  // enumLabel() — kanonický vzor je literální t("common.status.*").
  const statusLabels: Record<string, string> = {
    draft: t("common.status.draft"),
    published: t("common.status.published"),
  };

  const episodes = data?.data || [];

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("content.podcastEpisodes.list.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {t("content.podcastEpisodes.list.subtitle")}
          </p>
        </div>
        <Link to="/podcast-episodes/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          {t("content.podcastEpisodes.list.addEpisode")}
        </Link>
      </div>

      {episodes.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-12 text-center dark:border-gray-700">
          <Mic className="mx-auto mb-3 h-8 w-8 text-gray-400" />
          <p className="text-gray-500 dark:text-gray-400">
            {t("content.podcastEpisodes.list.empty")}
          </p>
        </div>
      ) : (
        <div className="card divide-y divide-gray-200 dark:divide-gray-700">
          {episodes.map((episode) => (
            <div key={episode.id} className="flex items-center justify-between py-3">
              <div className="min-w-0 flex-1 pr-4">
                <p className="truncate font-medium text-gray-900 dark:text-white">
                  {episode.title}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("content.podcastEpisodes.list.meta", {
                    date: episode.publishedAt || "—",
                    duration: formatEpisodeDuration(episode.durationSeconds) || "—",
                    status: statusLabels[episode.status] || episode.status,
                  })}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {episode.source === "youtube" && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                    {t("content.podcastEpisodes.list.fromYoutube")}
                  </span>
                )}
                {!!episode.manualOverride && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                    {t("content.podcastEpisodes.list.manualBadge")}
                  </span>
                )}
                <Link
                  to={`/podcast-episodes/${episode.id}`}
                  className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                >
                  <Pencil className="h-4 w-4" />
                </Link>
                <button
                  title={t("common.actions.delete")}
                  onClick={() => {
                    if (confirm(t("content.podcastEpisodes.list.confirmDelete")))
                      deleteMutation.mutate(episode.id);
                  }}
                  className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
